import { Plus, X } from "lucide-react"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/text-area"
import type { AgentMetric } from "@/types"

interface AgentMetricsFormProps {
  existingMetrics?: AgentMetric[]
  metrics: {
    id?: number
    uid?: string
    label: string
    description: string
    data_source_identifier: string
    _destroy?: boolean
  }[]
  onMetricsChange: (
    metrics: {
      id?: number
      uid?: string
      label: string
      description: string
      data_source_identifier: string
      _destroy?: boolean
    }[],
  ) => void
  errors?: Record<string, any>
}

export default function AgentMetricsForm({
  metrics,
  onMetricsChange,
  errors,
}: AgentMetricsFormProps) {
  const addMetric = () => {
    onMetricsChange([
      ...metrics,
      {
        label: "",
        description: "",
        data_source_identifier: "",
      },
    ])
  }

  const removeMetric = (index: number) => {
    const newMetrics = [...metrics]
    const metric = newMetrics[index]

    // If it has an id (existing record), mark it for destruction
    if (metric.id) {
      newMetrics[index] = { ...metric, _destroy: true }
    } else {
      // If it's a new metric, just remove it
      newMetrics.splice(index, 1)
    }

    onMetricsChange(newMetrics)
  }

  const updateMetric = (index: number, field: string, value: string) => {
    const newMetrics = [...metrics]
    newMetrics[index] = { ...newMetrics[index], [field]: value }
    onMetricsChange(newMetrics)
  }

  // Filter out destroyed metrics for display
  const visibleMetrics = metrics.filter((m) => !m._destroy)

  return (
    <div className="space-y-2">
      {/* Heading */}
      <div className="flex items-center gap-2">
        <Label className="text-sm font-medium">Unique metrics</Label>
      </div>

      {/* New Metrics Form */}
      {visibleMetrics.length > 0 && (
        <div className="space-y-4">
          {metrics.map((metric, index) => {
            if (metric._destroy) return null
            return (
              <div key={index} className="flex gap-2">
                <div className="flex-1 space-y-2">
                  <div className="grid gap-2">
                    <Label htmlFor={`data_source_${index}`} className="sr-only">
                      Data source
                    </Label>
                    <Input
                      id={`data_source_${index}`}
                      placeholder="Enter data source"
                      value={metric.data_source_identifier}
                      onChange={(e) =>
                        updateMetric(
                          index,
                          "data_source_identifier",
                          e.target.value,
                        )
                      }
                    />
                    <InputError
                      message={
                        errors?.[
                          `agent_metrics_attributes.${index}.data_source_identifier`
                        ] ||
                        errors?.[
                          `agent.agent_metrics_attributes.${index}.data_source_identifier`
                        ]
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor={`label_${index}`} className="sr-only">
                      Label
                    </Label>
                    <Input
                      id={`label_${index}`}
                      placeholder="Enter label"
                      value={metric.label}
                      onChange={(e) =>
                        updateMetric(index, "label", e.target.value)
                      }
                    />
                    <InputError
                      message={
                        errors?.[`agent_metrics_attributes.${index}.label`] ||
                        errors?.[
                          `agent.agent_metrics_attributes.${index}.label`
                        ]
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor={`description_${index}`} className="sr-only">
                      Tooltip
                    </Label>
                    <Textarea
                      id={`description_${index}`}
                      placeholder="Enter tooltip"
                      value={metric.description}
                      onChange={(e) =>
                        updateMetric(index, "description", e.target.value)
                      }
                      rows={2}
                      className="resize-none"
                    />
                    <InputError
                      message={
                        errors?.[
                          `agent_metrics_attributes.${index}.description`
                        ] ||
                        errors?.[
                          `agent.agent_metrics_attributes.${index}.description`
                        ]
                      }
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="mt-0 shrink-0"
                  onClick={() => removeMetric(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )
          })}
        </div>
      )}

      {/* Add Another Button */}
      <Button type="button" variant="outline" onClick={addMetric}>
        <Plus className="h-4 w-4" />
        Add another
      </Button>
    </div>
  )
}
